import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useBillingStore } from '@/stores/billingStore';
import Icon from './Icon';
import Wordmark from './Wordmark';
import AccountMenu from './AccountMenu';
import NotificationBell from './NotificationBell';
import { MOBILE_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';
import '../assets/css/Layout.css';

const SIDEBAR_KEY = 'mob-sidebar-collapsed';

const NAV = [
  { section: 'Menu', items: [
    { to: '/', icon: 'account_balance_wallet', label: 'Accounts', match: (p: string) => p === '/' || p.startsWith('/accounts') },
    { to: '/transactions', icon: 'swap_horiz', label: 'Transactions', match: (p: string) => p.startsWith('/transactions') },
  ]},
  { section: 'Billing', items: [
    { to: '/billing', icon: 'card_membership', label: 'Subscription', match: (p: string) => p === '/billing' },
    { to: '/billing/credits', icon: 'toll', label: 'Credits', match: (p: string) => p.startsWith('/billing/credits'), badgeKey: 'credits' },
  ]},
];

export const Layout = () => {
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { creditsBalance, fetchBillingStatus } = useBillingStore();
  const [collapsedPreference, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === '1'; } catch { return false; }
  });
  // On a phone the sidebar is a drawer over the page, closed until the menu button opens it;
  // the desktop icon-only collapse does not apply there.
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const collapsed = collapsedPreference && !isMobile;

  useEffect(() => { fetchBillingStatus(); }, [fetchBillingStatus]);

  // Picking a page, or widening past the phone breakpoint, closes the drawer.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname, isMobile]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const toggleSidebar = () => {
    if (isMobile) {
      setDrawerOpen((open) => !open);
      return;
    }
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const displayName = user?.name || user?.email || 'User';
  const displayEmail = user?.email || '';
  const initials = displayName.split(' ').map((p: string) => p[0]).join('').toUpperCase().slice(0, 2);

  const navLinkClass = (active: boolean) =>
    `flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-md transition-colors ${
      active
        ? 'bg-primary/10 text-primary font-semibold'
        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
    }`;

  return (
    // dvh, not vh: on a phone 100vh is taller than the visible screen (it ignores the browser's
    // toolbars), so the page itself scrolled a little on top of <main> and the list's end hid
    // behind the bottom bar.
    <div className="flex h-screen supports-[height:100dvh]:h-dvh bg-background-light dark:bg-background-dark">
      {/* Phone: the page behind the open drawer dims, and a tap on it closes the drawer */}
      {isMobile && drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40"
          onClick={() => setDrawerOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar: a column on desktop, a drawer sliding in from the left on a phone */}
      <aside
        className={
          isMobile
            ? `fixed inset-y-0 left-0 z-50 w-64 max-w-[85vw] bg-white dark:bg-slate-900 flex flex-col border-r border-slate-200 dark:border-slate-800 shadow-xl transition-transform duration-200 ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`
            : `${collapsed ? 'w-[76px]' : 'w-64'} flex-shrink-0 bg-white dark:bg-slate-900 flex flex-col border-r border-slate-200 dark:border-slate-800 transition-[width] duration-200`
        }
        aria-hidden={isMobile && !drawerOpen ? true : undefined}
        inert={isMobile && !drawerOpen ? true : undefined}
        data-testid="sidebar"
      >
        {/* Wordmark */}
        <div className={`h-16 flex items-center ${collapsed ? 'justify-center px-2' : 'px-5'} border-b border-slate-200 dark:border-slate-800`}>
          <Link to="/" aria-label="MagniOpenBanking" className="flex items-center">
            {collapsed ? <Wordmark symbolOnly className="text-2xl" /> : <Wordmark className="text-xl" />}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="px-3 py-5 flex-1 overflow-y-auto overscroll-y-contain">
          {NAV.map((group) => (
            <div key={group.section} className="mb-5 last:mb-0">
              {!collapsed && (
                <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  {group.section}
                </p>
              )}
              <ul className="space-y-1">
                {group.items.map((item) => {
                  const active = item.match(location.pathname);
                  const showBadge = 'badgeKey' in item && item.badgeKey === 'credits' && !collapsed;
                  return (
                    <li key={item.to}>
                      <Link to={item.to} className={navLinkClass(active)} title={collapsed ? item.label : undefined}>
                        <Icon name={item.icon} />
                        {!collapsed && <span className="flex-1">{item.label}</span>}
                        {showBadge && (
                          <span className="text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded-full">
                            {creditsBalance}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Account area (user + company/client rows + switcher) */}
        <div className="border-t border-slate-200 dark:border-slate-800 p-3">
          <AccountMenu
            collapsed={collapsed}
            displayName={displayName}
            displayEmail={displayEmail}
            initials={initials}
            onLogout={() => logout()}
          />
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="h-16 flex-shrink-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4">
          <button
            onClick={toggleSidebar}
            title={isMobile ? (drawerOpen ? 'Close menu' : 'Open menu') : collapsed ? 'Expand menu' : 'Collapse menu'}
            aria-label="Toggle menu"
            aria-expanded={isMobile ? drawerOpen : !collapsed}
            className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors"
          >
            <Icon name="panel_left" size={22} />
          </button>

          <div className="flex items-center gap-1">
            <NotificationBell />
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto overscroll-y-contain">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
